/**
 * Utility functions for keyboard event handling
 */

/**
 * Check if the user is currently typing in an input field.
 * Used to prevent app keyboard shortcuts from firing when user is typing elsewhere.
 */
export function htmlElementFromTarget(target: EventTarget | null | undefined): HTMLElement | null {
  if (target instanceof HTMLElement) return target;
  if (target instanceof Node) {
    const parent = target.parentElement;
    if (parent instanceof HTMLElement) return parent;
  }
  return null;
}

export function isTypingInInput(target: EventTarget | null): boolean {
  const el = htmlElementFromTarget(target);
  if (!el) {
    return false;
  }

  const tagName = el.tagName.toLowerCase();

  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true;
  }

  // Block shortcuts in ProseMirror editors — they handle their own shortcuts.
  // Exception: if the canvas (or slide) container has focus we still want canvas
  // shortcuts to fire, even when the canvas is mounted inside a ProseMirror root.
  if (el.closest('.ProseMirror')) {
    const isCanvasOrSlide = el.closest('.canvas-view-container') ||
                            el.closest('.slide-canvas');
    if (!isCanvasOrSlide) {
      return true; // Block — user is typing in ProseMirror
    }
    // Fall through — canvas has focus inside ProseMirror, allow canvas shortcuts
  }

  // Block shortcuts in contentEditable editors outside our canvas/slide contexts.
  if (el.isContentEditable) {
    const isAppCanvas = el.closest('.canvas-view-container') ||
                       el.closest('.slide-canvas');
    if (!isAppCanvas) {
      return true;
    }
  }

  if (el.getAttribute('role') === 'textbox') {
    return true;
  }

  return false;
}

/**
 * Check if a keyboard event should be handled by the app.
 * Returns true if the event should be handled, false if it should be ignored.
 */
export function shouldHandleKeyboardEvent(e: KeyboardEvent): boolean {
  if (isTypingInInput(e.target)) {
    return false;
  }
  return true;
}
